import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Search, X, Eye, User, SlidersHorizontal, CheckCircle2, XCircle, Plus, Upload } from 'lucide-react';
import { fetchMocPatients, setMocPatientStatus } from '../../redux/mocSlice';
import MocAddPatientModal from './MocAddPatientModal';
import MocBulkUploadModal from './MocBulkUploadModal';
import { getTenantOptions } from '../../redux/tenantSlice';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { FilterPanel, FilterSection, FilterOptionList, FilterSearchableList, FilterPopover } from '../../components/common/FilterPanel';
import { formatDate } from './mocCommon';
import useCanManage from '../../hooks/useCanManage';

// PATIENTS — the platform's ONE patient list (owner's merge, Aug 2026).
//
// There used to be two sidebar entries showing "the patients": "Mitra One
// Care" (this screen — one row per GLOBAL identity) and "Patients" (one row
// per hospital record, so the same person appeared once per hospital). The
// owner ruled they are the same thing seen twice, so the per-hospital list is
// gone and this screen — the identity list — took its name, its route
// (/patients) and its LOOK: the tenant app's patient table verbatim (gradient
// h1, Pending/Active/Disabled pill-tabs, teal gradient header row, initials
// avatars, DOB and Gender columns, the status toggle, the Eye). The view
// behind the Eye is MocPatientDetail — hospital picker, six tabs, activity
// timeline, break-glass — exactly what the Mitra One Care option had.
//
// WHAT THE COLUMNS MEAN HERE. A row is ONE PERSON — one `patients` row of
// `kind='moc'` (doc/one-patient-row.md §2), never one row per hospital. DOB
// and Gender are that person's own values, read straight off the row: there is
// no "most recently linked hospital record" to borrow them from any more, so
// the old dob/gender fallback is gone from this screen and from the API's
// answer. A person who has given neither shows a dash. The subtitle under the
// name is the Mitra One Care ID — the one handle that works at every hospital,
// i.e. what support asks for. A hospital's own patient number is a property of
// the ALLOCATION, so it belongs on the detail page's hospital list, not here.
//
// THE TENANT'S TABS, RE-GROUNDED. The tenant's Pending tab means "profile
// incomplete"; a person row carries no single profileStatus (each hospital has
// its own view of completeness), so the closest true statement of "signed up
// but not really on the platform yet" is: active, with no hospital allocation.
// Every person lands in exactly one tab:
//     Pending    status active, 0 live allocations      (maxLinks=0)
//     Active     status active, ≥1 live allocation      (minLinks=1)
//     Disabled   status inactive                        (any allocations)
// Because the tabs now own the allocation-count axis, the old "Hospital links"
// filter is gone from the popover; Hospital (where the person holds a live
// allocation) and Gender took its place, alongside the MOC-specific
// Verification filter.
//
// Header actions (owner, 2026-09-02): Add + Bulk Upload are BACK, super-admin
// style — both create GLOBAL MOC accounts only (never tenant rows), and bulk
// creation exists ONLY here (the tenant app's bulk upload was deleted the
// same day). Dropped from the tenant ROW on purpose: the password and
// MOC-link modals and Bills (all per-tenant workflows — the view page carries
// their MOC counterparts as tabs). What is left on a row — open, and
// activate / deactivate — is the whole of what a platform owner does there.

const STATUS_TABS = [
    { key: 'pending', labelKey: 'patientpage.inactive', fallback: 'Pending' },
    { key: 'active', labelKey: 'patientpage.active', fallback: 'Active' },
    { key: 'disabled', labelKey: 'patientpage.disabled', fallback: 'Disabled' },
];

// The server predicate behind each tab — see the header. minLinks/maxLinks are
// the same numeric bounds the old links filter used; the tabs simply pin them.
const TAB_PARAMS = {
    pending: { status: 'active', maxLinks: 0 },
    active: { status: 'active', minLinks: 1 },
    disabled: { status: 'inactive' },
};

// FILTERS ARE SERVER-SIDE, ALL OF THEM — filtering and counting happen on the
// same query or `total` lies to the pager. Do not add a `.filter()` pass over
// `patients` here again.
const VERIFICATION_OPTIONS = [
    { value: 'email', labelKey: 'moc.email_verified', fallback: 'Email verified' },
    { value: 'phone', labelKey: 'moc.phone_verified', fallback: 'Phone verified' },
    { value: 'both', labelKey: 'moc.both_verified', fallback: 'Email and phone verified' },
    { value: 'none', labelKey: 'moc.nothing_verified', fallback: 'Nothing verified' },
];

const GENDER_OPTIONS = [
    { value: 'male', labelKey: 'patientpage.male', fallback: 'Male' },
    { value: 'female', labelKey: 'patientpage.female', fallback: 'Female' },
    { value: 'Not_Communicated', labelKey: 'common.gender.notCommunicated', fallback: 'Not Communicated' },
    { value: 'other', labelKey: 'patientpage.other', fallback: 'Other' },
];

const optionLabel = (options, value, t) => {
    const found = options.find((o) => o.value === value);
    return found ? t(found.labelKey, found.fallback) : value;
};

const EMPTY_FILTERS = { verified: '', gender: [], hospital: [] };

const initials = (name, surname) =>
    `${name ? name.charAt(0).toUpperCase() : ''}${surname ? surname.charAt(0).toUpperCase() : ''}`;

// --- MODULE-LEVEL SUBCOMPONENTS -------------------------------------------
// Never nested inside the page component: an inline component is a new type on
// every render, React remounts its subtree, and the search input loses focus
// after one keystroke. This repo has shipped that bug more than once.

const Avatar = ({ row }) => {
    const text = initials(row.name, row.surname);
    // The person's ONE photo (on the `patients` row, presigned by the API).
    // There is no per-hospital second photo any more (§10a), so nothing here
    // has to choose between two. Initials stay the fallback, and onError
    // covers an expired presigned URL.
    const [broken, setBroken] = useState(false);
    if (row.image && !broken) {
        return (
            <img
                src={row.image}
                alt=""
                onError={() => setBroken(true)}
                className="w-10 h-10 rounded-full object-cover shadow-md flex-shrink-0"
            />
        );
    }
    return (
        <div className="relative w-10 h-10 flex-shrink-0">
            <div className="flex absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md">
                {text
                    ? <span className="text-white text-sm font-bold uppercase">{text}</span>
                    : <User className="w-5 h-5 text-white" />}
            </div>
        </div>
    );
};

const VerifiedTick = ({ verified, verifiedLabel, unverifiedLabel }) => (
    verified
        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" title={verifiedLabel} />
        : <XCircle className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" title={unverifiedLabel} />
);

const StatusToggle = ({ row, busy, onToggle }) => (
    <div
        data-testid={`moc-status-toggle-${row.mocAccountId}`}
        className={`relative inline-flex h-6 w-11 sm:h-7 sm:w-12 items-center rounded-full transition-colors duration-300 ${row.status === 'active' ? 'bg-[#4ABDBD]' : 'bg-gray-300'} ${busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !busy && onToggle(row)}
    >
        <span className={`inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${row.status === 'active' ? 'translate-x-[24px]' : 'translate-x-1'}`} />
    </div>
);

const GenderPill = ({ gender, t }) => (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 text-[#3AA8A8] border border-[#4ABDBD]/20">
        {gender ? t(`patientpage.${gender}`, gender) : '-'}
    </span>
);

// "How did this patient join?" — stamped at account-create time (owner,
// 2026-09-02): the patient registered themselves, a hospital's desk / bulk
// upload minted the identity, or (reserved) the super admin did. Accounts
// from before the stamp carry NULLs and render as a dash — never a guess.
const JoinedVia = ({ joinedVia, t }) => {
    const via = joinedVia?.via;
    if (via === 'register') {
        return <span className="text-xs font-semibold text-[#2D8A8A]">{t('moc.joined_register', 'By register')}</span>;
    }
    if (via === 'super_admin') {
        return <span className="text-xs font-semibold text-[#8a5f26]">{t('moc.joined_super_admin', 'By super admin')}</span>;
    }
    if (via === 'tenant') {
        const who = [joinedVia.role, joinedVia.name].filter(Boolean).join(' — ');
        return (
            <span className="inline-flex min-w-0 flex-col">
                <span className="text-xs font-semibold text-gray-700 truncate">
                    {t('moc.joined_by', 'By {{hospital}}', { hospital: joinedVia.tenantName || t('moc.hr_detail_hospital', 'Hospital') })}
                </span>
                {who && <span className="text-[10px] text-gray-400 truncate">{who}</span>}
            </span>
        );
    }
    return <span className="text-xs text-gray-400">—</span>;
};

const FilterChip = ({ label, value, onRemove, testId }) => (
    <div data-testid={testId} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
        <span className="text-xs text-gray-600">
            <span className="font-semibold">{label}:</span> {value}
        </span>
        <button
            type="button"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
        >
            <X className="h-3.5 w-3.5" />
        </button>
    </div>
);

export default function MocPatients() {
    const { t } = useTranslation(['patientpage']);
    const dispatch = useDispatch();
    // Status switches turn read-only without the manage key — the API refuses
    // the PATCH anyway (hooks/useCanManage.js).
    const canManage = useCanManage('patients.manage');
    const navigate = useNavigate();
    const { patients, total, listLoading, listError, statusUpdating } = useSelector((s) => s.moc);
    const { tenantOptions = [] } = useSelector((s) => s.tenant);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // The stored value predates the tab re-grounding on some browsers ('', or
    // 'inactive' from the old Active/Inactive/All set) — anything unknown falls
    // back to 'active' rather than rendering a tab that no longer exists.
    const [activeTab, setActiveTab] = useState(() => {
        const stored = sessionStorage.getItem('saPatientsTab');
        return TAB_PARAMS[stored] ? stored : 'active';
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isBulkOpen, setIsBulkOpen] = useState(false);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const inputRef = useRef(null);
    const filterRef = useRef(null);
    const filterButtonRef = useRef(null);

    useEffect(() => {
        sessionStorage.setItem('saPatientsTab', activeTab);
    }, [activeTab]);

    // tenantId -> hospital name for the filter list and its chips.
    useEffect(() => {
        dispatch(getTenantOptions());
    }, [dispatch]);

    // Debounce. Resetting to page 1 belongs HERE, with the term change — doing it
    // in the fetch effect would fire a second request per keystroke.
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(id);
    }, [searchTerm]);

    useEffect(() => {
        dispatch(fetchMocPatients({
            search: debouncedSearch,
            ...(TAB_PARAMS[activeTab] || {}),
            verified: filters.verified,
            gender: filters.gender.join(','),
            tenantId: filters.hospital.map((h) => h.tenantId).join(','),
            page: currentPage,
            limit: itemsPerPage,
        }));
    }, [dispatch, debouncedSearch, activeTab, filters, currentPage, itemsPerPage, refreshKey]);

    useEffect(() => {
        if (isFilterOpen) setTempFilters({ ...filters });
    }, [isFilterOpen, filters]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (
                filterRef.current && !filterRef.current.contains(event.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(event.target)
            ) {
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [filters]);

    // The API already returned exactly the rows that match every filter, and
    // `total` counts the same set — so this is the page, unmodified.
    const rows = patients || [];
    const hasFilters = Boolean(filters.verified || filters.gender.length || filters.hospital.length);
    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // Any filter change is a new result set, so it starts at page 1 — otherwise
    // an operator on page 7 applies a filter with 2 pages of results and lands
    // on an empty screen that looks like "no matches".
    const applyFilters = (next) => {
        setFilters(next);
        setCurrentPage(1);
    };

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const handleStatusToggle = async (row) => {
        const next = row.status === 'active' ? 'inactive' : 'active';
        const { isConfirmed } = await Swal.fire({
            title: next === 'inactive'
                ? t('moc.deactivate_title', 'Deactivate this identity?')
                : t('moc.activate_title', 'Activate this identity?'),
            // Deactivating locks the person out of EVERY connected hospital at
            // once, which is the part that is easy to under-estimate from a row
            // that looks like an ordinary on/off switch.
            text: next === 'inactive'
                ? t('moc.deactivate_text', 'They will not be able to sign in to the Mitra One Care patient app for any connected hospital. Their records are untouched.')
                : t('moc.activate_text', 'They will be able to sign in to the Mitra One Care patient app again.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: next === 'inactive' ? '#dc2626' : '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: next === 'inactive'
                ? t('moc.deactivate', 'Deactivate')
                : t('moc.activate', 'Activate'),
            cancelButtonText: t('patientpage.cancel', 'Cancel'),
        });
        if (!isConfirmed) return;

        const result = await dispatch(setMocPatientStatus({ mocAccountId: row.mocAccountId, status: next }));
        if (setMocPatientStatus.fulfilled.match(result)) {
            toast.success(next === 'active'
                ? t('moc.status_activated', 'Account activated')
                : t('moc.status_deactivated', 'Account deactivated'));
            // The row may no longer belong on this tab, so refetch rather than
            // leaving it sitting under a heading it now contradicts.
            setRefreshKey((k) => k + 1);
        } else {
            toast.warn(result.payload?.error || t('moc.status_failed', 'Failed to update the account status'));
        }
    };

    // ONE action on a row: open (owner, 2026-09-02 — shortcut buttons that
    // all landed on the same detail page read as clutter, so password /
    // links / bills live as that page's tabs and nothing else).
    const open = (row) => navigate(`/patients/${row.mocAccountId}`);

    if (listError && !listLoading && (patients || []).length === 0) {
        return <ErrorPage error={{ status: 500 }} pageName={t('patientpage.heading', 'Patients')} />;
    }
    // A failed fetch with rows ALREADY on screen is worse than an empty error
    // page: the store keeps the previous page, so the table would sit under the
    // active filter chips showing rows from a different filter set — precisely
    // the "silently show unfiltered data" failure moving the filters
    // server-side was meant to end. Rows stay visible (they are real data), but
    // the mismatch is said out loud instead of implied by nothing.
    const staleRows = Boolean(listError) && !listLoading && (patients || []).length > 0;

    const verifiedLabel = t('moc.verified', 'Verified');
    const unverifiedLabel = t('moc.unverified', 'Not verified');

    return (
        <div data-testid="moc-patients-page" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header — the tenant patient page's exact h1 + its two action
                buttons, super-admin flavoured: both create GLOBAL MOC accounts
                only, and bulk creation lives ONLY here (owner, 2026-09-02). */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="moc-patients-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('patientpage.heading', 'Patients')}
                </h1>
                {canManage && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                        <button
                            data-testid="moc-bulk-upload-patient-button"
                            onClick={() => setIsBulkOpen(true)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 border border-[#4ABDBD] text-[#3AA8A8] rounded-xl shadow-sm hover:bg-[#ecfdfd] transition-all duration-300 font-semibold text-sm"
                        >
                            <Upload className="w-5 h-5" />
                            <span>{t('patientpage.bulk_upload', 'Bulk Upload')}</span>
                        </button>
                        <button
                            data-testid="moc-add-patient-button"
                            onClick={() => setIsAddOpen(true)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm"
                        >
                            <Plus className="w-5 h-5" />
                            <span>{t('patientpage.add_patient', 'Add Patient')}</span>
                        </button>
                    </div>
                )}
            </div>

            {staleRows && (
                <div data-testid="moc-stale-rows-banner" className="mb-4 flex items-start gap-2.5 rounded-xl border-l-4 border-red-400 bg-red-50 p-3.5">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <p className="text-xs leading-relaxed text-red-700">
                        {t('moc.stale_rows', 'The last refresh failed, so the rows below are from the previous search and may not match the filters shown. Retry by changing any filter or reloading the page.')}
                    </p>
                </div>
            )}

            {/* Status tabs + search + filter */}
            <div data-testid="moc-search-container" className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="flex gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 relative">
                    {STATUS_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            data-testid={`moc-patient-tab-${tab.key}`}
                            onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
                            className={`relative px-6 py-2 font-medium text-sm rounded-xl transition-all duration-300 z-10 ${activeTab === tab.key ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab.key && (
                                <motion.div
                                    layoutId="activeMocPatientStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{t(tab.labelKey, tab.fallback)}</span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="moc-patients-search"
                        type="text"
                        placeholder={t('moc.search_placeholder', 'Search by name, email or phone')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="relative w-full sm:w-auto">
                    <button
                        ref={filterButtonRef}
                        data-testid="moc-filter-button"
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        aria-expanded={isFilterOpen}
                        aria-label={t('patientpage.filter', 'Filter')}
                        className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters(EMPTY_FILTERS);
                                    applyFilters(EMPTY_FILTERS);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    applyFilters(tempFilters);
                                    setIsFilterOpen(false);
                                }}
                                clearLabel={t('patientpage.clear', 'Clear')}
                                applyLabel={t('patientpage.apply', 'Apply')}
                            >
                                {/* Hospital — which hospitals the identity holds a
                                    live link to. The Super Admin-only axis. */}
                                <FilterSection title={t('patientpage.hospital', 'Hospital')} isActive={tempFilters.hospital.length > 0}>
                                    <FilterSearchableList
                                        multiple
                                        items={tenantOptions || []}
                                        value={tempFilters.hospital}
                                        onChange={(val) => setTempFilters({ ...tempFilters, hospital: val })}
                                        labelKey="name"
                                        sublabelKey="__none__"
                                        valueKey="tenantId"
                                        searchPlaceholder={t('common.search', 'Search...')}
                                        noResultsText={t('patientpage.no_hospitals_found', 'No hospitals found')}
                                        allLabel={t('patientpage.all_hospitals', 'All Hospitals')}
                                    />
                                </FilterSection>

                                <FilterSection title={t('patientpage.gender', 'Gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={GENDER_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey, o.fallback) }))}
                                    />
                                </FilterSection>

                                <FilterSection title={t('moc.verification', 'Verification')} isActive={Boolean(tempFilters.verified)}>
                                    <FilterOptionList
                                        value={tempFilters.verified}
                                        onChange={(val) => setTempFilters({ ...tempFilters, verified: val })}
                                        options={VERIFICATION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey, o.fallback) }))}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* Applied filter chips */}
            {hasFilters && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2">
                            {t('patientpage.applied_filters', 'Applied Filters')}:
                        </span>
                        {filters.hospital.map((h) => (
                            <FilterChip
                                key={`hospital-${h.tenantId}`}
                                testId={`moc-applied-filter-hospital-${h.tenantId}`}
                                label={t('patientpage.hospital', 'Hospital')}
                                value={h.name || t('moc.hr_detail_hospital', 'Hospital')}
                                onRemove={() => applyFilters({ ...filters, hospital: filters.hospital.filter((x) => x.tenantId !== h.tenantId) })}
                            />
                        ))}
                        {filters.gender.map((g) => (
                            <FilterChip
                                key={`gender-${g}`}
                                testId={`moc-applied-filter-gender-${g}`}
                                label={t('patientpage.gender', 'Gender')}
                                value={optionLabel(GENDER_OPTIONS, g, t)}
                                onRemove={() => applyFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                            />
                        ))}
                        {filters.verified && (
                            <FilterChip
                                testId={`moc-applied-filter-verification-${filters.verified}`}
                                label={t('moc.verification', 'Verification')}
                                value={optionLabel(VERIFICATION_OPTIONS, filters.verified, t)}
                                onRemove={() => applyFilters({ ...filters, verified: '' })}
                            />
                        )}
                        {/* The count is the WHOLE filtered set, not this page —
                            that is the point of moving the filters server-side. */}
                        <span className="text-[11px] text-gray-500">
                            {t('moc.filter_total_count', '{{total}} matching', { total })}
                        </span>
                        <button
                            data-testid="moc-clear-all-filters"
                            onClick={() => { applyFilters(EMPTY_FILTERS); setTempFilters(EMPTY_FILTERS); }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('patientpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {listLoading ? (
                <div data-testid="moc-patients-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('patientpage.loading', 'Loading…')}</span>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="moc-patients-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.full_name', 'Full Name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.email', 'Email')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.phone', 'Phone')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.dob', 'DOB')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.gender', 'Gender')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('moc.joined_via', 'Joined via')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('patientpage.status_label', 'Status')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('patientpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? rows.map((row, index) => (
                                    <tr
                                        key={row.mocAccountId}
                                        data-testid={`moc-patient-row-${row.mocAccountId}`}
                                        onClick={() => open(row)}
                                        className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group cursor-pointer"
                                    >
                                        <td className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{indexOfFirst + index + 1}</td>
                                        <td className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                            <div className="flex items-center gap-3">
                                                <Avatar row={row} />
                                                <span className="inline-flex min-w-0 flex-col">
                                                    <span data-testid={`moc-patient-name-${row.mocAccountId}`} className="font-medium text-gray-800">
                                                        {[row.name, row.surname].filter(Boolean).join(' ') || t('moc.unnamed', 'Unnamed identity')}
                                                    </span>
                                                    {/* No Mitra One Care id on the row: owner rule
                                                        29 Sep 2026, no system id is drawn on screen. */}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                {row.email || '-'}
                                                {row.email && <VerifiedTick verified={row.emailVerified} verifiedLabel={verifiedLabel} unverifiedLabel={unverifiedLabel} />}
                                            </span>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                {row.phone || '-'}
                                                {row.phone && <VerifiedTick verified={row.phoneVerified} verifiedLabel={verifiedLabel} unverifiedLabel={unverifiedLabel} />}
                                            </span>
                                        </td>
                                        <td data-testid={`moc-patient-dob-${row.mocAccountId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                            {row.dob ? formatDate(row.dob) : '-'}
                                        </td>
                                        <td className="p-3 text-xs">
                                            <GenderPill gender={row.gender} t={t} />
                                        </td>
                                        <td data-testid={`moc-patient-joined-via-${row.mocAccountId}`} className="p-3 text-xs max-w-[12rem]">
                                            <JoinedVia joinedVia={row.joinedVia} t={t} />
                                        </td>
                                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                                            <StatusToggle row={row} busy={statusUpdating || !canManage} onToggle={handleStatusToggle} />
                                        </td>
                                        <td className="p-3 text-xs" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    data-testid={`moc-patient-view-button-${row.mocAccountId}`}
                                                    onClick={() => open(row)}
                                                    title={t('moc.action_view', 'View')}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                                                    <Eye className="h-4 w-4 relative z-10" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr data-testid="moc-patients-empty">
                                        <td colSpan="9" className="p-3 text-center text-gray-500">
                                            {debouncedSearch || hasFilters
                                                ? t('moc.no_match', 'No patient matches this search.')
                                                : t('patientpage.no_patients_found', 'No patients found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div data-testid="moc-patients-mobile-cards" className="block sm:hidden space-y-4">
                        {rows.length > 0 ? rows.map((row, index) => (
                            <div key={row.mocAccountId} data-testid={`moc-patient-mobile-card-${row.mocAccountId}`} className="p-4 bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs font-bold text-[#4ABDBD]">
                                        {t('patientpage.serial_no', 'S.No')}: {indexOfFirst + index + 1}
                                    </span>
                                    <button
                                        data-testid={`moc-patient-mobile-view-${row.mocAccountId}`}
                                        onClick={() => open(row)}
                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3 text-sm font-semibold text-gray-800">
                                        <Avatar row={row} />
                                        <div className="min-w-0">
                                            <div className="truncate">{[row.name, row.surname].filter(Boolean).join(' ') || t('moc.unnamed', 'Unnamed identity')}</div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-bold">{t('patientpage.email', 'Email')}:</span> {row.email || '-'}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-bold">{t('patientpage.phone', 'Phone')}:</span> {row.phone || '-'}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-bold">{t('patientpage.dob', 'DOB')}:</span> {row.dob ? formatDate(row.dob) : '-'}
                                    </div>
                                    <div className="text-xs">
                                        <span className="font-bold">{t('patientpage.gender', 'Gender')}:</span>{' '}
                                        <GenderPill gender={row.gender} t={t} />
                                    </div>
                                    <div className="text-xs flex items-center gap-2">
                                        <span className="font-bold">{t('moc.joined_via', 'Joined via')}:</span>{' '}
                                        <JoinedVia joinedVia={row.joinedVia} t={t} />
                                    </div>
                                    <div className="text-xs flex items-center gap-2">
                                        <span className="font-bold">{t('patientpage.status_label', 'Status')}:</span>
                                        <StatusToggle row={row} busy={statusUpdating || !canManage} onToggle={handleStatusToggle} />
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="p-3 text-center text-gray-500">
                                {t('patientpage.no_patients_found', 'No patients found')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!listLoading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={rows.length === 0 ? 'disable' : ''}
                />
            )}

            <MocAddPatientModal
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
                onComplete={() => setRefreshKey((k) => k + 1)}
            />
            <MocBulkUploadModal
                isOpen={isBulkOpen}
                onClose={() => setIsBulkOpen(false)}
                onComplete={() => setRefreshKey((k) => k + 1)}
            />
        </div>
    );
}
